import React from 'react';
import { Icon, type IconName } from './Icon';

export interface SideNavItem<K extends string = string> {
    key: K;
    label: string;
    icon?: IconName;
    /** Right-aligned, muted: a count («3»), a marker of unsaved changes, a lock. */
    trailing?: React.ReactNode;
    /** Greyed while not current — something the plan does not include, which
     *  still opens (its `trailing` usually carries the `PlanLockPill`). */
    muted?: boolean;
}

export interface SideNavGroup<K extends string = string> {
    key: string;
    label: string;
    icon?: IconName;
    items: SideNavItem<K>[];
}

export interface SideNavProps<K extends string = string> {
    /** A flat list… */
    items?: SideNavItem<K>[];
    /** …or groups: a muted header and its items, indented. A group of ONE
     *  renders in its place as a single top-level item, with the group's label
     *  and icon (the Configuración navs: «IA» is a group with one tab). An
     *  empty group renders nothing. */
    groups?: SideNavGroup<K>[];
    /** The item shown as current (`aria-current`); `null` = none. */
    active: K | null;
    onSelect: (key: K) => void;
    /** Accessible name of the `<nav>`. */
    ariaLabel: string;
    className?: string;
}

const itemBase = 'w-full flex items-center gap-2.5 rounded-lg text-sm text-left transition-colors duration-100';
const activeCls = 'bg-primary/10 text-primary font-medium';
const hoverCls = 'hover:bg-slate-100 dark:hover:bg-surface-hover-dark';
const mutedCls = 'text-slate-400 dark:text-slate-500';

/**
 * SideNav — the left-hand list of sections of a page or a settings screen
 * (Standards §20.10): one item current, the rest one click away.
 *
 * The current item sits on a soft primary tint, the others plain with a
 * hover. Only the colour moves, and quickly: an action `Button` here (its
 * press scale and its slow `transition-all`) made the old item fade out while
 * the new one faded in, so for a moment two looked selected (owner,
 * 2026-10-24).
 *
 * In a list of groups the top-level items are medium weight, like the group
 * headers they sit between, and the indented ones a shade lighter.
 *
 * It does not decide WHAT selecting does — a section shown, a URL param, a
 * scroll to an anchor — nor how it looks on a phone: a page with a long list
 * usually swaps it for a `DropdownSelect` below `lg`.
 */
export default function SideNav<K extends string = string>({
    items, groups, active, onSelect, ariaLabel, className = '',
}: SideNavProps<K>) {
    const renderItem = (
        item: SideNavItem<K>,
        level: 'flat' | 'top' | 'nested',
        reactKey: string = item.key,
    ) => {
        const current = item.key === active;
        const idle = item.muted
            ? `${mutedCls} ${hoverCls}`
            : level === 'nested'
                ? `text-slate-500 dark:text-slate-400 ${hoverCls}`
                : `text-slate-600 dark:text-slate-300 ${hoverCls}`;
        const weight = level === 'top' ? 'font-medium' : '';
        return (
            <button
                key={reactKey}
                type="button"
                onClick={() => onSelect(item.key)}
                aria-current={current ? 'page' : undefined}
                className={`${itemBase} ${weight} ${level === 'nested' ? 'pl-11 pr-3 py-1.5' : 'px-3 py-2'} ${current ? activeCls : idle}`}
            >
                {item.icon && level !== 'nested' ? <Icon name={item.icon} className="!text-[20px] shrink-0" aria-hidden="true" /> : null}
                <span className="min-w-0 flex-1 break-words">{item.label}</span>
                {item.trailing != null ? (
                    <span className={`shrink-0 text-xs tabular-nums ${current ? '' : 'text-slate-400 dark:text-slate-500'}`}>{item.trailing}</span>
                ) : null}
            </button>
        );
    };

    return (
        <nav aria-label={ariaLabel} className={`flex flex-col gap-0.5 ${className}`}>
            {items?.map((item) => renderItem(item, 'flat'))}
            {groups?.map((group) => {
                if (group.items.length === 0) return null;
                if (group.items.length === 1) {
                    const only = group.items[0]!;
                    return renderItem({ ...only, label: group.label, icon: group.icon }, 'top', group.key);
                }
                return (
                    <div key={group.key} className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                            {group.icon ? <Icon name={group.icon} className="!text-[20px] shrink-0" aria-hidden="true" /> : null}
                            {group.label}
                        </div>
                        {group.items.map((item) => renderItem(item, 'nested'))}
                    </div>
                );
            })}
        </nav>
    );
}
